"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import { LitElement, html, nothing } from "lit";
import { query, state } from "lit/decorators.js";
import { STCComponentMixin } from "../../../common/mixins/stc_component_mixin";
import { htmlImportZipIsHtmlPath } from "./microsite_html_file_view";
async function readZip(buffer) {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const decoder = new TextDecoder("utf-8", { fatal: false });
  let eocdOffset = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65558); i--) {
    if (view.getUint32(i, true) === 101010256) {
      eocdOffset = i;
      break;
    }
  }
  if (eocdOffset === -1) return { ok: false, error: "Not a valid ZIP file." };
  const cdCount = view.getUint16(eocdOffset + 10, true);
  const cdOffset = view.getUint32(eocdOffset + 16, true);
  const entries = [];
  let pos = cdOffset;
  for (let i = 0; i < cdCount; i++) {
    if (view.getUint32(pos, true) !== 33639248) break;
    const compression = view.getUint16(pos + 10, true);
    const compressedSize = view.getUint32(pos + 20, true);
    const fnLen = view.getUint16(pos + 28, true);
    const extraLen = view.getUint16(pos + 30, true);
    const commentLen = view.getUint16(pos + 32, true);
    const localOffset = view.getUint32(pos + 42, true);
    const fileName = decoder.decode(bytes.slice(pos + 46, pos + 46 + fnLen));
    pos += 46 + fnLen + extraLen + commentLen;
    if (fileName.endsWith("/") || fileName.includes("__MACOSX/") || fileName.toLowerCase().endsWith("/.ds_store") || fileName.toLowerCase() === ".ds_store" || (fileName.split("/").pop() ?? "").startsWith("._")) {
      continue;
    }
    if (view.getUint32(localOffset, true) !== 67324752) continue;
    const lfhFnLen = view.getUint16(localOffset + 26, true);
    const lfhExtraLen = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + lfhFnLen + lfhExtraLen;
    const compressedData = bytes.slice(dataStart, dataStart + compressedSize);
    let decompressed;
    if (compression === 0) {
      decompressed = compressedData;
    } else if (compression === 8) {
      try {
        const ds = new DecompressionStream("deflate-raw");
        const writer = ds.writable.getWriter();
        const reader = ds.readable.getReader();
        writer.write(compressedData);
        writer.close();
        const chunks = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) chunks.push(value);
        }
        let totalLen = 0;
        for (const c of chunks) totalLen += c.length;
        decompressed = new Uint8Array(totalLen);
        let off = 0;
        for (const c of chunks) {
          decompressed.set(c, off);
          off += c.length;
        }
      } catch {
        continue;
      }
    } else {
      continue;
    }
    entries.push({ path: fileName.replace(/\\/g, "/").replace(/^\.\/+/, ""), bytes: decompressed });
  }
  if (entries.length === 0) return { ok: false, error: "No files found in the ZIP." };
  return { ok: true, entries };
}
function normalizeHtml(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const isFullDoc = /^\s*<!doctype\s+html/i.test(trimmed) || /^\s*<html[\s>]/i.test(trimmed);
  return isFullDoc ? trimmed : `<!DOCTYPE html>
<html><head><meta charset="utf-8"/></head><body>
${trimmed}
</body></html>`;
}
const HTML_IMPORT_DIALOG_TAG = "stc-html-import-dialog";
const HTML_IMPORT_FORM_ID = "eab-html-import-form";
const HTML_IMPORT_HI_FILE_SHADOW_STYLE_ID = "eab-html-import-hi-file-overrides";
const HTML_IMPORT_HI_FILE_SHADOW_CSS = `
.input-wrapper, .file-input-wrapper {
  min-height: 4rem;
}
`;
export function openHtmlImportDialog(options) {
  let el = document.querySelector(HTML_IMPORT_DIALOG_TAG);
  if (!el) {
    el = document.createElement(HTML_IMPORT_DIALOG_TAG);
    document.body.appendChild(el);
  }
  el.open(options?.panel);
}
export function micrositesAiHtmlEnabled() {
  const el = document.getElementById("eab-microsite-boot-data");
  if (!el?.textContent) return false;
  try {
    const j = JSON.parse(el.textContent);
    return j.microsites_ai_assistance_enabled === true && j.is_html === true;
  } catch {
    return false;
  }
}
function parsePositiveInt(raw) {
  if (raw === null || raw === void 0) return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}
function coercePageId(raw) {
  if (raw === null || raw === void 0) return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
}
function readAiHtmlGenerationIds() {
  const bootEl = document.getElementById("eab-microsite-boot-data");
  let bootMicrositeId = null;
  if (bootEl?.textContent) {
    try {
      const j = JSON.parse(bootEl.textContent);
      bootMicrositeId = parsePositiveInt(j.microsite_id);
    } catch {
    }
  }
  const win = window;
  const ctx = win.__eabMicrositePagesContext;
  let micrositeId = bootMicrositeId;
  if (ctx?.micrositeId != null && parsePositiveInt(ctx.micrositeId) != null) {
    if (bootMicrositeId == null || ctx.micrositeId === bootMicrositeId) {
      micrositeId = ctx.micrositeId;
    }
  }
  let currentPageId = null;
  if (ctx != null && ctx.currentPageId != null) {
    const fromCtx = coercePageId(ctx.currentPageId);
    if (fromCtx != null) currentPageId = fromCtx;
  }
  if (currentPageId == null) {
    const mgr = document.querySelector("stc-microsite-page-manager");
    if (mgr && "currentPageId" in mgr) {
      const v = mgr.currentPageId;
      const fromMgr = coercePageId(v);
      if (fromMgr != null) currentPageId = fromMgr;
    }
  }
  return { micrositeId, currentPageId };
}
function notifyMicrositeHtmlImportUser(payload) {
  const win = window;
  if (win.HIP?.fn?.notify) {
    win.HIP.fn.notify(payload);
    return;
  }
  window.alert(payload.message);
}
export class MicrositeHtmlImportDialog extends STCComponentMixin(LitElement) {
  constructor() {
    super(...arguments);
    this._opened = false;
    this._method = "";
    this._pastedHtml = "";
    this._error = null;
    this._loading = false;
    this._genPrompt = "";
    this._genUseReference = false;
    this._genSubmitting = false;
    this._srcdoc = "";
    this._fileName = "";
    this._zipFiles = {};
    /** After a successful import we `close()` the dialog; `close-ended` must not emit `html-import:cancel`. */
    this._skipCancelOnNextCloseEnded = false;
  }
  // ── Public API ────────────────────────────────────────────────────────────
  open(initialPanel) {
    this._skipCancelOnNextCloseEnded = false;
    this._resetUploadState();
    if ((initialPanel === "generate_html" || initialPanel === "generate_ai") && micrositesAiHtmlEnabled()) {
      this._method = "generate_ai";
    }
    this._opened = true;
  }
  close() {
    this._opened = false;
  }
  updated(changed) {
    super.updated(changed);
    if (this._method === "upload") {
      void this.updateComplete.then(() => this._syncImportFileShadowStyles());
    }
  }
  // ── Private helpers ───────────────────────────────────────────────────────
  /** Apply compact drop-zone sizing inside hi-file shadow root (global CSS cannot reach). */
  _syncImportFileShadowStyles() {
    const root = this._importFileEl?.shadowRoot;
    if (!root) return;
    let style = root.getElementById(HTML_IMPORT_HI_FILE_SHADOW_STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = HTML_IMPORT_HI_FILE_SHADOW_STYLE_ID;
      style.className = "eab-html-import__hi-file-shadow";
      root.appendChild(style);
    }
    if (style.textContent !== HTML_IMPORT_HI_FILE_SHADOW_CSS) {
      style.textContent = HTML_IMPORT_HI_FILE_SHADOW_CSS;
    }
  }
  _clearImportFile() {
    const el = this._importFileEl;
    if (el) {
      el.userFiles = [];
      el.otherFiles = [];
    }
  }
  _resetPreparedImport() {
    this._srcdoc = "";
    this._fileName = "";
    this._zipFiles = {};
  }
  _resetUploadState() {
    this._method = "";
    this._pastedHtml = "";
    this._error = null;
    this._loading = false;
    this._genPrompt = "";
    this._genUseReference = false;
    this._genSubmitting = false;
    this._resetPreparedImport();
    this._clearImportFile();
  }
  _dispatch(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, { bubbles: true, composed: true, detail: detail ?? null })
    );
  }
  // ── User actions ──────────────────────────────────────────────────────────
  async _handleGenerateNavigateAiHtml() {
    const prompt = this._genPrompt.trim();
    if (!prompt) {
      notifyMicrositeHtmlImportUser({ message: "Enter a prompt.", status: "error" });
      return;
    }
    const { micrositeId, currentPageId } = readAiHtmlGenerationIds();
    if (micrositeId == null) {
      notifyMicrositeHtmlImportUser({
        message: "Microsite context is missing. Reload the build page and try again.",
        status: "error"
      });
      return;
    }
    const win = window;
    let extra = {};
    if (this._genUseReference) {
      const snap = win.__eabMicrositeEditorSnapshot;
      if (snap == null || typeof snap !== "object" || Array.isArray(snap)) {
        notifyMicrositeHtmlImportUser({
          message: "Could not read the current page from the editor. Make an edit in the builder or clear \u201CUse current page as reference\u201D.",
          status: "error"
        });
        return;
      }
      try {
        extra = { context_current_page_json: JSON.stringify(snap) };
      } catch {
        notifyMicrositeHtmlImportUser({
          message: "Could not serialize the current page. Try again after a small edit.",
          status: "error"
        });
        return;
      }
    }
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content ?? "";
    this._genSubmitting = true;
    let resultId;
    try {
      const res = await fetch(`/microsites/sites/${micrositeId}/ai_generation`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf
        },
        credentials: "same-origin",
        body: JSON.stringify({ ai_generation: { prompt, ...extra } })
      });
      const postData = await res.json();
      if (!res.ok || postData.status !== "processing" || typeof postData.result_id !== "string") {
        notifyMicrositeHtmlImportUser({
          message: typeof postData.error === "string" ? postData.error : `Request failed (${res.status})`,
          status: "error"
        });
        this._genSubmitting = false;
        return;
      }
      resultId = postData.result_id;
    } catch (err) {
      notifyMicrositeHtmlImportUser({
        message: err instanceof Error ? err.message : "Generation failed",
        status: "error"
      });
      this._genSubmitting = false;
      return;
    }
    const channel = window.user_notifications;
    if (!channel) {
      notifyMicrositeHtmlImportUser({
        message: "Real-time channel unavailable. Reload the page and try again.",
        status: "error"
      });
      this._genSubmitting = false;
      return;
    }
    const requestedPageId = this._genUseReference ? currentPageId : null;
    const forceNewPage = !this._genUseReference;
    const handlers = {};
    const cleanup = () => {
      if (handlers.ready) channel.unbind("microsite_ai_generation_ready", handlers.ready);
      if (handlers.error) channel.unbind("microsite_ai_generation_error", handlers.error);
      this._genSubmitting = false;
    };
    const onReady = async (raw) => {
      const d = raw;
      if (d.result_id !== resultId) return;
      cleanup();
      if (typeof d.url !== "string") {
        notifyMicrositeHtmlImportUser({ message: "Invalid result from server.", status: "error" });
        return;
      }
      try {
        const s3Res = await fetch(d.url);
        const payload = await s3Res.json();
        if (typeof payload.content !== "string" || !payload.content.trim()) {
          notifyMicrositeHtmlImportUser({ message: "The server returned an empty response.", status: "error" });
          return;
        }
        if (payload.output_mode !== "html") {
          notifyMicrositeHtmlImportUser({
            message: "The server did not return HTML. Try again or contact support.",
            status: "error"
          });
          return;
        }
        window.dispatchEvent(
          new CustomEvent("microsite:ai-generation-result", {
            detail: {
              output_mode: "html",
              content: payload.content,
              pages: payload.pages,
              requested_page_id: requestedPageId,
              force_new_page: forceNewPage
            }
          })
        );
        notifyMicrositeHtmlImportUser({ message: "AI HTML was applied to the page.", status: "success" });
        this._skipCancelOnNextCloseEnded = true;
        this.close();
      } catch (err) {
        notifyMicrositeHtmlImportUser({
          message: err instanceof Error ? err.message : "Failed to load generation result.",
          status: "error"
        });
      }
    };
    const onError = (raw) => {
      const d = raw;
      if (d.result_id !== resultId) return;
      cleanup();
      notifyMicrositeHtmlImportUser({
        message: typeof d.error === "string" ? d.error : "AI generation failed.",
        status: "error"
      });
    };
    handlers.ready = (data) => {
      void onReady(data);
    };
    handlers.error = onError;
    channel.bind("microsite_ai_generation_ready", handlers.ready);
    channel.bind("microsite_ai_generation_error", handlers.error);
  }
  _submitLocked() {
    if (this._method === "generate_ai") return this._genSubmitting;
    if (this._method === "upload") return this._loading;
    return false;
  }
  _submitLabel() {
    return this._genSubmitting && this._method === "generate_ai" ? "Generating\u2026" : "Save HTML";
  }
  async _handleFormSubmit(_e) {
    this._error = null;
    if (!this._method) {
      this._error = "Please select a method.";
      return;
    }
    if (this._method === "generate_ai") {
      await this._handleGenerateNavigateAiHtml();
      return;
    }
    if (this._method === "paste") {
      const paste = this._pastedHtml.trim();
      if (!paste) {
        this._error = "Please paste HTML.";
        return;
      }
      const srcdoc = normalizeHtml(paste);
      if (!srcdoc) {
        this._error = "No HTML content found.";
        return;
      }
      this._finishImport(srcdoc, "", {});
      return;
    }
    if (this._method !== "upload") return;
    if (this._loading) return;
    if (!this._srcdoc) {
      const el = this._importFileEl;
      const files = el?.userFiles ?? el?.files ?? [];
      if (!files[0]) {
        this._error = "Please upload an .html, .htm, or .zip file.";
        return;
      }
      this._error = "The file is still being read. Try again in a moment.";
      return;
    }
    this._finishImport(this._srcdoc, this._fileName, this._zipFiles);
  }
  _finishImport(srcdoc, fileName, zipFiles) {
    this._skipCancelOnNextCloseEnded = true;
    this._dispatch("html-import:complete", { srcdoc, fileName, zipFiles });
    this.close();
  }
  _handleTextareaInput(e) {
    const path = e.composedPath();
    const inner = path.find((n) => n instanceof HTMLTextAreaElement);
    const host = e.currentTarget;
    if (inner) {
      this._pastedHtml = inner.value;
    } else {
      const v = host?.value;
      this._pastedHtml = typeof v === "string" ? v : v != null ? String(v) : "";
    }
    this._error = null;
  }
  _handleImportFileChanged() {
    const el = this._importFileEl;
    const files = el?.userFiles ?? el?.files ?? [];
    const file = files[0];
    if (!file) {
      this._resetPreparedImport();
      return;
    }
    const lower = file.name.toLowerCase();
    if (lower.endsWith(".zip")) {
      void this._loadZipFile(file);
      return;
    }
    if (lower.endsWith(".html") || lower.endsWith(".htm")) {
      this._loadHtmlFile(file);
      return;
    }
    this._error = "Please choose an .html, .htm, or .zip file.";
    this._resetPreparedImport();
    this._clearImportFile();
  }
  _handleMethodChange(e) {
    const detail = e.detail;
    const target = e.target;
    const val = (detail?.value ?? target?.value ?? "").trim();
    if (val !== "paste" && val !== "upload" && val !== "generate_ai") return;
    if (this._method === val) return;
    this._method = val;
    this._error = null;
    this._pastedHtml = "";
    this._genPrompt = "";
    this._resetPreparedImport();
    this._clearImportFile();
  }
  _handleGenPromptInput(e) {
    const path = e.composedPath();
    const inner = path.find((n) => n instanceof HTMLTextAreaElement);
    const host = e.currentTarget;
    if (inner) {
      this._genPrompt = inner.value;
    } else {
      const v = host?.value;
      this._genPrompt = typeof v === "string" ? v : v != null ? String(v) : "";
    }
    this._error = null;
  }
  _methodOptions() {
    const options = [
      { value: "paste", label: "Paste HTML Code" },
      { value: "upload", label: "Upload HTML File" }
    ];
    if (micrositesAiHtmlEnabled()) {
      options.push({ value: "generate_ai", label: "Generate HTML with AI" });
    }
    return options;
  }
  _renderMethodSelect() {
    const options = this._methodOptions();
    return html`
      <hi-input-group class="eab-html-import__method-select">
        <hi-label for="eab-html-import-method" required>Select Method</hi-label>
        <hi-dropdown
          id="eab-html-import-method"
          kind="value"
          placeholder="Select method"
          cap-height
        >
          <hi-select
            id="eab-html-import-method-select"
            name="import_method"
            form=${HTML_IMPORT_FORM_ID}
            aesthetic="dropdown"
            mode="value"
            required
            .value=${this._method || ""}
            @selected-ended=${this._handleMethodChange}
          >
            ${options.map(
      (o) => html`
                <hi-option value=${o.value} ?selected=${this._method === o.value}>
                  ${o.label}
                </hi-option>
              `
    )}
          </hi-select>
        </hi-dropdown>
      </hi-input-group>
    `;
  }
  _renderPasteFields() {
    return html`
      <hi-input-group>
        <hi-label for="eab-html-import-paste">HTML</hi-label>
        <hi-code
          id="eab-html-import-paste"
          class="eab-html-editor-dialog__text-area"
          name="html"
          form=${HTML_IMPORT_FORM_ID}
          language="javascript"
          required
          .value=${this._pastedHtml}
          @value-changed=${this._handleTextareaInput}
        ></hi-code>
      </hi-input-group>
    `;
  }
  _renderUploadFields() {
    return html`
      <hi-text class="eab-html-editor-dialog__description mt-5 display-block">
        You can upload either a single HTML file or upload a ZIP file (each HTML file will create a page.) For ZIP imports, include any CSS, JS, images, videos, or fonts and use paths relative to each HTML file. Full documents are imported as-is, while fragments are wrapped with minimal <html>/<body> tags during import. The next step will show the code editor and the rendered preview. To edit, you will need to use the code editor; it will not be converted to content blocks.
      </hi-text>
      <hi-input-group class="eab-html-import__file-upload">
        <hi-label for="eab-html-import-file">Attachments</hi-label>
        <hi-file
          id="eab-html-import-file"
          class="eab-html-import__hi-file"
          name="attachment"
          form=${HTML_IMPORT_FORM_ID}
          accept=".html,.htm,.zip"
          max-files="1"
          required
          ?disabled=${this._loading}
          @value-changed=${this._handleImportFileChanged}
        ></hi-file>
      </hi-input-group>
    `;
  }
  _renderGenerateAiFields() {
    return html`
      <hi-input-group>
        <hi-label for="eab-html-import-ai-prompt" required>
          AI Prompt
          <hi-icon id="eab-html-import-ai-prompt-info" kind="info_circle" theme="blue"></hi-icon>
          <hi-tooltip for="eab-html-import-ai-prompt-info">This feature uses the AI large language model (LLM) configured in the Global Administration settings to generate content based on your prompt. By using this feature, you acknowledge and agree to the use of AI-generated content. Do not enter sensitive, confidential, personal, or proprietary information in your prompt. AI-generated content may be inaccurate or incomplete and should be reviewed before use.</hi-tooltip>
        </hi-label>
        <hi-textarea
          id="eab-html-import-ai-prompt"
          class="eab-html-editor-dialog__text-area"
          name="ai_prompt"
          form=${HTML_IMPORT_FORM_ID}
          type="text"
          rows="8"
          placeholder="Example: A landing page for our summer bridge program with a hero, three feature cards, student testimonials, and a contact section."
          required
          .value=${this._genPrompt}
          spellcheck="true"
          autocomplete="off"
          @input=${this._handleGenPromptInput}
        ></hi-textarea>
      </hi-input-group>
    `;
  }
  _renderMethodFields() {
    if (!this._method) return nothing;
    let fields;
    if (this._method === "upload") {
      fields = this._renderUploadFields();
    } else if (this._method === "generate_ai" && micrositesAiHtmlEnabled()) {
      fields = this._renderGenerateAiFields();
    } else if (this._method === "paste") {
      fields = this._renderPasteFields();
    } else {
      return nothing;
    }
    return html`<div class="eab-html-import__method-fields">${fields}</div>`;
  }
  _renderFormBody() {
    return html`
      <h2 class="eab-html-import__section-heading">Choose Method</h2>
      <hi-text class="eab-html-editor-dialog__description display-block">
        ${micrositesAiHtmlEnabled() ? "You can paste HTML, upload a HTML/zip file or generate HTML with an AI prompt to start building your website." : "You can paste HTML or upload a HTML/zip file to start building your website."}
        Once the HTML is added, you can see a preview and edit the HTML as needed in the editor.
      </hi-text>
      ${this._renderMethodSelect()}
      ${this._renderMethodFields()}
      ${this._error ? html`<p class="eab-html-editor-dialog__error" role="alert">${this._error}</p>` : nothing}
    `;
  }
  _loadHtmlFile(file) {
    this._loading = true;
    this._error = null;
    const reader = new FileReader();
    reader.onload = () => {
      const raw = typeof reader.result === "string" ? reader.result : "";
      const srcdoc = normalizeHtml(raw);
      if (!srcdoc) {
        this._error = "The file appears to be empty.";
        this._loading = false;
        return;
      }
      this._srcdoc = srcdoc;
      this._fileName = file.name;
      this._zipFiles = {};
      this._pastedHtml = "";
      this._error = null;
      this._loading = false;
    };
    reader.onerror = () => {
      this._error = "Could not read the file.";
      this._loading = false;
    };
    reader.readAsText(file, "utf-8");
  }
  async _loadZipFile(file) {
    this._loading = true;
    this._error = null;
    try {
      const buffer = await file.arrayBuffer();
      const result = await readZip(buffer);
      if (!result.ok) {
        this._error = result.error;
        this._loading = false;
        return;
      }
      const zipFiles = {};
      for (const entry of result.entries) {
        zipFiles[entry.path] = entry.bytes;
      }
      const htmlPaths = result.entries.filter((e) => htmlImportZipIsHtmlPath(e.path)).map((e) => e.path).sort();
      if (htmlPaths.length === 0) {
        this._error = "No HTML files (.html or .htm) found in the ZIP.";
        this._loading = false;
        return;
      }
      const firstHtml = htmlPaths[0];
      const htmlBytes = zipFiles[firstHtml];
      const htmlText = new TextDecoder("utf-8", { fatal: false }).decode(htmlBytes);
      const srcdoc = normalizeHtml(htmlText);
      if (!srcdoc) {
        this._error = "The HTML file in the ZIP appears to be empty.";
        this._loading = false;
        return;
      }
      this._zipFiles = zipFiles;
      this._srcdoc = srcdoc;
      this._fileName = file.name;
      this._pastedHtml = "";
      this._error = null;
      this._loading = false;
    } catch (err) {
      this._error = err instanceof Error ? err.message : "Failed to read ZIP file.";
      this._loading = false;
    }
  }
  _handleDialogClose() {
    this._opened = false;
    if (this._skipCancelOnNextCloseEnded) {
      this._skipCancelOnNextCloseEnded = false;
      this._resetUploadState();
      return;
    }
    this._dispatch("html-import:cancel");
    this._resetUploadState();
  }
  // ── Main render ───────────────────────────────────────────────────────────
  render() {
    return html`
      <hi-dialog
        ?opened=${this._opened}
        @close-ended=${this._handleDialogClose}
        wide-screen
        aesthetic="prompt"
      >
        <h2 slot="heading" class="mb-0">Add HTML to Website Builder</h2>

        <hi-form
          id=${HTML_IMPORT_FORM_ID}
          form=${HTML_IMPORT_FORM_ID}
          disable-http
          @submit-ended=${(e) => void this._handleFormSubmit(e)}
          @cancel-ended=${() => this.close()}
        >
          ${this._renderFormBody()}

          <hi-button
            slot="controls"
            type="submit"
            aesthetic="primary"
            ?locked=${this._submitLocked()}
          >
            <span>${this._submitLabel()}</span>
          </hi-button>
          <hi-button slot="controls" type="cancel" aesthetic="ghost">
            <span>Cancel</span>
          </hi-button>
        </hi-form>
      </hi-dialog>
    `;
  }
}
__decorateClass([
  query("#eab-html-import-file")
], MicrositeHtmlImportDialog.prototype, "_importFileEl", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_opened", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_method", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_pastedHtml", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_error", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_loading", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_genPrompt", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_genUseReference", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_genSubmitting", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_srcdoc", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_fileName", 2);
__decorateClass([
  state()
], MicrositeHtmlImportDialog.prototype, "_zipFiles", 2);
customElements.define(HTML_IMPORT_DIALOG_TAG, MicrositeHtmlImportDialog);
